// src/components/display/DisplayHeader.js
import React from 'react';
import { Button } from 'baseui/button';
import { Settings, Maximize, Minimize } from 'lucide-react';
import QRCode from 'qrcode.react';
import {
  InfoHeaderArea,
  InfoHeaderLeftCell,
  InfoHeaderCenterCell,
  InfoHeaderRightCell,
  InfoHeaderLevelText,
  InfoHeaderTimerText,
  RightInfoText,
  InfoLabelCell,
  InfoValueCell,
  TournamentTitleLayout,
  TournamentNameText,
  HeaderActionsContainer,
  createHeaderActionButtonOverrides,
  StartsAtContainer,
  StartsAtLabelText,
  StartsAtDateText,
  StartsAtTimeText,
  EmbeddedQrCodeContainer,
} from '../../styles/displayHeaderStyles';
import {
  QrCodeIcon,
} from '../../styles/iconStyles';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { formatTimer, parseTime, calculateAverageStack, calculateFutureDate } from '../../utils/viewHelpers';

const calculateNextBreakTime = (levels, currentLevel, currentRemainingTime, parseTimeFunc, timeKey) => {
  if (!levels || !levels[currentLevel] || !timeKey) return 0;
  const currentLvlData = levels[currentLevel];
  if (currentLvlData.type === 'break') {
    return 0;
  }
  let timeAccumulator = currentRemainingTime;
  let breakFound = false;
  for (let i = currentLevel + 1; i < levels.length; i++) {
    const nextLvl = levels[i];
    if (nextLvl.type === 'break') {
      breakFound = true;
      break;
    }
    timeAccumulator += parseTimeFunc(nextLvl[timeKey] || "0:00");
  }
  return breakFound ? timeAccumulator : 0;
};


const DisplayHeader = ({
  palette,
  structure,
  tournamentDetails,
  tournamentName,
  remainingTime,
  rowIndex,
  currentTime,
  isFullscreen,
  isPortrait,
  showQrCode,
  toggleFullScreen,
  onOpenSettings,
  onToggleQrCode,
  t,
  children,
  iconSize,
}) => {
  const { config: appConfig } = useAppConfig();
  if (!structure) return null;

  const levelKey = appConfig.requiredLevelKey;
  const timeKeyColumn = structure.columns.find(c => c.label === appConfig.requiredTimeKey);
  const timeKey = timeKeyColumn ? timeKeyColumn.label : null;

  const playerCountDisplay = tournamentDetails.showPlayerStats 
    ? `${Math.max(0, tournamentDetails.initialEntries - tournamentDetails.knockouts + tournamentDetails.totalRebuys)}/${tournamentDetails.initialEntries + tournamentDetails.totalRebuys}` 
    : t('text.notAvailable'); 
  
  const averageStackDisplay = tournamentDetails.showPlayerStats
    ? calculateAverageStack(tournamentDetails).toLocaleString()
    : t('text.notAvailable'); 
  
  const nextBreakTimeSec = structure.levels ? calculateNextBreakTime(structure.levels, rowIndex, remainingTime, parseTime, timeKey) : 0;
  
  const hasLevels = structure && structure.levels && structure.levels.length > 0;
  const currentLevelData = hasLevels ? structure.levels[rowIndex] : null;

  const EIGHT_HOURS_IN_SECONDS = 28800;
  const isFirstRowBreak = rowIndex === 0 && currentLevelData?.type === 'break';
  const showStartsAtDisplay = isFirstRowBreak && remainingTime > EIGHT_HOURS_IN_SECONDS;
  
  let startsAtDate = null;
  if (showStartsAtDisplay) {
    startsAtDate = calculateFutureDate(remainingTime);
  }

  return (
    <InfoHeaderArea $palette={palette}>
      <TournamentTitleLayout>
        <TournamentNameText $palette={palette}>
          {tournamentName || t('tournament.namePlaceholder')}
        </TournamentNameText>
        <HeaderActionsContainer>
          {!isPortrait && onToggleQrCode && (
             <Button size="mini" onClick={onToggleQrCode} title={t('display.toggleQrCodeTooltip')} kind="tertiary" shape="circle" overrides={createHeaderActionButtonOverrides(palette)}>
                <QrCodeIcon $size={iconSize} />
            </Button>
          )}
          {toggleFullScreen && (
            <Button size="mini" onClick={toggleFullScreen} kind="tertiary" shape="circle" title={isFullscreen ? t('display.exitFullscreen') : t('display.enterFullscreen')} overrides={createHeaderActionButtonOverrides(palette)}>
                {isFullscreen ? <Minimize size={iconSize} /> : <Maximize size={iconSize} />}
            </Button>
          )}
          {onOpenSettings && (
            <Button size="mini" onClick={onOpenSettings} title={t('common.settings')} kind="tertiary" shape="circle" overrides={createHeaderActionButtonOverrides(palette)}>
                <Settings size={iconSize} />
            </Button>
          )}
        </HeaderActionsContainer>
      </TournamentTitleLayout>

      <InfoHeaderLeftCell $palette={palette}>
        <InfoLabelCell $palette={palette}>{t('display.currentTimeLabel')}</InfoLabelCell>
        <InfoValueCell $palette={palette}>{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</InfoValueCell>
        <InfoLabelCell $palette={palette}>{t('display.nextBreakLabel')}</InfoLabelCell>
        <InfoValueCell $palette={palette}>{nextBreakTimeSec > 0 ? formatTimer(nextBreakTimeSec) : t('text.notAvailable')}</InfoValueCell>
        <InfoLabelCell $palette={palette}>{t('player.countLabelOnly')}</InfoLabelCell>
        <InfoValueCell $palette={palette}>{playerCountDisplay}</InfoValueCell>
        <InfoLabelCell $palette={palette}>{t('player.averageStackLabelOnly')}</InfoLabelCell>
        <InfoValueCell $palette={palette}>{averageStackDisplay}</InfoValueCell>
      </InfoHeaderLeftCell>

      <InfoHeaderCenterCell $palette={palette}>
        {showStartsAtDisplay && startsAtDate ? (
          <StartsAtContainer>
            <StartsAtLabelText $palette={palette}>{t('display.startsAtLabel')}</StartsAtLabelText>
            <StartsAtDateText $palette={palette}>
              {startsAtDate.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}
            </StartsAtDateText>
            <StartsAtTimeText $palette={palette}>
              {startsAtDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
            </StartsAtTimeText>
          </StartsAtContainer>
        ) : (
          hasLevels && currentLevelData ? (
            <>
              <InfoHeaderLevelText $palette={palette}>
                {currentLevelData.type === 'break'
                    ? (rowIndex === 0 ? t('display.countdownToStart') : t('common.break'))
                    : `${t('common.level')} ${currentLevelData[levelKey]}`
                }
              </InfoHeaderLevelText>
              <InfoHeaderTimerText $palette={palette} data-testid="timer-display">
                {formatTimer(remainingTime)}
              </InfoHeaderTimerText>
            </>
          ) : (
            <InfoHeaderLevelText $palette={palette}>
              {t('display.noStructureLoaded')}
            </InfoHeaderLevelText>
          )
        )}
      </InfoHeaderCenterCell>

      <InfoHeaderRightCell $palette={palette}>
        {children ? (
          children
        ) : (
          <>
            <RightInfoText $palette={palette}>
              {tournamentDetails.customInfoText && tournamentDetails.customInfoText.trim() !== ''
                ? tournamentDetails.customInfoText.split('\n').map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))
                : t('text.notAvailable')}
            </RightInfoText>
            {showQrCode && (
                <EmbeddedQrCodeContainer>
                    <QRCode value={window.location.href} size={64} />
                </EmbeddedQrCodeContainer>
            )}
          </>
        )}
      </InfoHeaderRightCell>
    </InfoHeaderArea>
  );
};

export default DisplayHeader;